import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Color } from '../../app/StyleGuide';
import { Button, MediumPrimaryButton, PrimaryButton } from '../../components/Buttons';
import ConfirmationModal from '../../components/ConfirmationModal';
import EmptyState from '../../components/EmptyState';
import ErrorMessage from '../../components/ErrorMessage';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, FullWidthCard } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import { ModalWarningList } from '../../components/ModalElements';
import { SessionDateHeaderAndDetails, SessionDescription } from '../../components/SessionElements';
import useCurriculum from '../../hooks/useCurriculum';
import useModal from '../../hooks/useModal';
import alertService from '../../services/AlertService';
import sessionTimelineService from '../../services/sessionTimelineService';
import windowService from '../../services/windowService';
import { handleError } from '../../utils/apiUtils';
import { ymdFromDate } from '../../utils/dateUtils';
import { sanitizeHTML } from '../../utils/sanitizeHTML';
import { useUser } from '../authentication';
import { type Session, type Timeline, DownloadPresentationMediaButton, SessionTimeline } from '../curriculum';
import { getCurriculumSchedule } from './api/getCurriculumSchedule';
import { HeaderNavigation } from './components/HeaderNavigation';
import { LeaderTimelinesOverlay } from '../curriculum';
import styled from 'styled-components';

const TextLink = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    background-color: ${Color.Gray._20};
  }
`;

type UrlParams = { curriculumId: string; sessionId: string; timelineId: string; originalTimelineId: string };

type State = {
  isLoading: boolean;
  error?: Error;
  timeline?: Timeline;
  session?: Session;
  timelines: Timeline[];
};

const PreviewButton = ({ url }: { url: string }) => (
  <PrimaryButton onClick={() => windowService.openRoute(url, true)}>
    <i className="fas fa-external-link-alt" /> View as Group Leader
  </PrimaryButton>
);

const PaddedContainer = styled(Container)`
  padding-bottom: ${Grid._5};
`;

export function ManageBibleStudySessionTimeline() {
  const { brand, ageCategory } = useCurriculum();
  const { curriculumId, sessionId, timelineId, originalTimelineId } = useParams<UrlParams>();
  const [data, setData] = useState<State>({
    isLoading: true,
    timelines: [],
  });
  const { user } = useUser();

  const scheduleRoute = `#/manage-bible-study/schedule/${brand.code}/${ageCategory}/${curriculumId}`;
  const crumbs = [
    { name: 'Curriculum', route: '#/bible-studies' },
    {
      name: `${brand.name}: ${ageCategory}`,
      route: scheduleRoute,
    },
    { name: 'Leader Guide' },
  ];

  const redirectToResetTimeline = (resetTimelineId: string) => {
    windowService.redirectTo(
      `#/manage-bible-study/schedule/${brand.code}/${ageCategory}/${curriculumId}/${sessionId}/timeline/${originalTimelineId}/${resetTimelineId}`
    );
  };

  const submitTimelineResetAndRedirect = () => {
    sessionTimelineService
      .deleteCustomizedTimeline(originalTimelineId, timelineId, user?.lastSelectedAccount, user?.userId)
      .then(() =>
        sessionTimelineService.createCustomizedTimeline(
          data.timeline?.name,
          originalTimelineId,
          user?.lastSelectedAccount,
          user?.userId
        )
      )
      .then((timeline: { id: string }) => {
        redirectToResetTimeline(timeline.id);
        alertService.showOnNextPage('Leader Guide Reset');
      })
      .catch(handleError);
  };

  const handleSessionTimelinesUpdate = (sessionId: string, timelines: Timeline[]) => {
    const currentTimelineStillExists = timelines.some(t => t.id === timelineId);

    if (!currentTimelineStillExists) {
      const nextTimeline = timelines.find(t => t.originalTimelineId);
      if (nextTimeline) {
        alertService.showOnNextPage('Leader Guide Removed');
        windowService.redirectTo(
          `#/manage-bible-study/schedule/${brand.code}/${ageCategory}/${curriculumId}/${sessionId}/timeline/${nextTimeline.originalTimelineId}/${nextTimeline.id}`
        );
      } else {
        alertService.showOnNextPage('Leader Guide Removed');
        windowService.redirectTo(scheduleRoute);
      }
      return;
    }

    setData(prev => {
      const updatedCurrentTimeline = timelines.find(t => t.id === prev.timeline?.id);
      return {
        ...prev,
        timelines,
        session: prev.session ? { ...prev.session, timelines } : prev.session,
        timeline:
          updatedCurrentTimeline && prev.timeline
            ? { ...prev.timeline, name: updatedCurrentTimeline.name }
            : prev.timeline,
      };
    });
  };

  const [modal, openModal] = useModal(
    (type: 'timelines' | 'resetModal', payload: Session, dismissModal: () => void) => {
      if (!type) return;
      if (type === 'timelines') {
        return (
          <LeaderTimelinesOverlay
            brandCode={brand.code}
            ageCategory={ageCategory}
            session={payload}
            updateSessionTimelines={handleSessionTimelinesUpdate}
            handleDismiss={dismissModal}
          />
        );
      } else if (type === 'resetModal') {
        return (
          <ConfirmationModal
            buttonActionText="Reset Leader Guide"
            buttonType="danger"
            title="Reset Leader Guide"
            prompt={
              <span>
                This will delete your notes, toggle all the sections on, and restore the original sequence of events.
              </span>
            }
            subtext={'Are you sure?'}
            handleSubmit={submitTimelineResetAndRedirect}
            handleDismiss={dismissModal}
          >
            <ModalWarningList warnings={['This action cannot be undone']} />
          </ConfirmationModal>
        );
      }
    }
  );

  useEffect(() => {
    setData(prev => ({ ...prev, isLoading: true }));
    Promise.all([
      getCurriculumSchedule(curriculumId),
      sessionTimelineService.getPublishedAdminSessionTimeline(originalTimelineId, timelineId),
    ])
      .then(([schedule, timeline]) => {
        const session = schedule.sessions.find((s: Session) => s.sessionId === sessionId);
        setData({ session, timeline, isLoading: false, timelines: session?.timelines ?? [] });
      })
      .catch((error: Error) => {
        console.error(error);
        setData({ isLoading: false, error, timelines: [] });
      });
  }, [timelineId]);

  return (
    <>
      <HeaderNavigation
        crumbs={crumbs}
        brandCode={brand.code}
        ageCategory={ageCategory}
        curriculumId={curriculumId}
        orgId={user?.lastSelectedAccount}
        activeTab="schedule"
      />
      <FullWidthCard style={{ paddingBottom: Grid._5 }}>
        {data.isLoading ? (
          <PaddedContainer>
            <LoadingState />
          </PaddedContainer>
        ) : !data.error && !data.session ? (
          <PaddedContainer>
            <EmptyState
              title="We are unable to find your session"
              description="Please return to the schedule and try again"
            >
              <MediumPrimaryButton
                style={{ marginTop: Grid._4 }}
                onClick={() => windowService.redirectTo(scheduleRoute)}
              >
                Return to Schedule
              </MediumPrimaryButton>
            </EmptyState>
          </PaddedContainer>
        ) : data.error ? (
          <PaddedContainer>
            <ErrorMessage />
          </PaddedContainer>
        ) : (
          <>
            <Container data-qa-hook="manageTimelineViewContainer">
              <HelpAndInfoBox
                iconClass="fas fa-chalkboard-teacher"
                title="Session Leader Guide"
                description="Here you can customize this session’s leader guide to fit your church’s time by including or excluding activities. You can also add notes to activities to give your leaders additional information."
              />

              <SessionDateHeaderAndDetails session={data.session}>
                <p>{data.session?.getAvailableThroughMessage('MMMM d, yyyy')}</p>
              </SessionDateHeaderAndDetails>
              <SessionDescription
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.session?.description ?? '') }}
              ></SessionDescription>
            </Container>

            <SessionTimeline
              key={timelineId}
              curriculumName={ageCategory}
              timeline={data.timeline}
              session={data.session}
              customizable
              useViewPreferences={false}
              title={<TextLink onClick={() => openModal('timelines', data.session)}>{data.timeline?.name}</TextLink>}
              subtitle="Manage session activities and add notes for your leaders"
              actionButtons={
                <>
                  <PreviewButton
                    url={`/curriculum/${brand.code}/${ageCategory}/session/${sessionId}/issue/${data.session?.issueId}/timeline/${originalTimelineId}/${timelineId}/preview`}
                  />

                  <DownloadPresentationMediaButton
                    curriculumId={curriculumId}
                    timelineId={originalTimelineId}
                    timelineCustomizationId={timelineId}
                    curriculumName={brand.name}
                    curriculumAgeCategory={ageCategory}
                    sessionScheduledDate={ymdFromDate(data.session?.scheduledDate)}
                    timelineName={data.timeline?.name}
                    ButtonComponent={Button}
                  />
                  <Button onClick={() => openModal('resetModal')}>Reset Leader Guide</Button>
                </>
              }
            />
          </>
        )}
        {modal}
      </FullWidthCard>
    </>
  );
}
